import React, { useState, useEffect } from 'react';
import {
  Globe, Search, Activity, CheckCircle, XCircle, Monitor,
  Shield, AlertTriangle, MapPin, Clock, Smartphone,
  TrendingUp, Zap, Ban, Unlock, Hash, UserX, UserCheck,
  ChevronRight, ArrowLeft, BarChart3, Wifi, Layers, Target,
  Fingerprint, Server, Radio
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const THREAT_LEVELS = {
  critical: { label: 'CRITICAL', color: '#ff0000', bg: 'rgba(255,0,0,0.15)', range: [80, 100] },
  high: { label: 'HIGH', color: '#ff4444', bg: 'rgba(255,68,68,0.12)', range: [60, 79] },
  medium: { label: 'MEDIUM', color: '#ff9800', bg: 'rgba(255,152,0,0.12)', range: [40, 59] },
  low: { label: 'LOW', color: '#ffeb3b', bg: 'rgba(255,235,59,0.1)', range: [20, 39] },
  minimal: { label: 'MINIMAL', color: '#4caf50', bg: 'rgba(76,175,80,0.1)', range: [0, 19] },
};

export default function VisitorManifest() {
  const [view, setView] = useState('list'); // 'list' | 'ip-detail'
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ event_type: '', outcome: '', search: '' });

  // IP lookup state
  const [ipLookup, setIpLookup] = useState('');
  const [ipData, setIpData] = useState(null);
  const [ipLoading, setIpLoading] = useState(false);
  const [selectedIP, setSelectedIP] = useState(null);

  const loadManifest = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 50,
        ...(filters.event_type && { event_type: filters.event_type }),
        ...(filters.outcome && { outcome: filters.outcome }),
        ...(filters.search && { search: filters.search }),
      });
      const res = await api.get(`/api/admin/manifest?${params.toString()}`);
      if (pageNum === 1) {
        setEntries(res.data.entries);
      } else {
        setEntries(prev => [...prev, ...res.data.entries]);
      }
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
    } catch (err) {
      toast.error('Failed to load manifest');
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/api/admin/manifest/stats');
      setStats(res.data);
    } catch {}
  };

  useEffect(() => { loadStats(); loadManifest(1); }, []);
  useEffect(() => { setPage(1); loadManifest(1); }, [filters]);

  const handleIpLookup = async () => {
    if (!ipLookup.trim()) return;
    setIpLoading(true);
    try {
      const res = await api.get(`/api/admin/manifest/ip/${ipLookup.trim()}`);
      setIpData(res.data);
      setSelectedIP(ipLookup.trim());
      setView('ip-detail');
    } catch { toast.error('IP lookup failed'); }
    setIpLoading(false);
  };

  const handleIpClick = async (ip) => {
    setIpLookup(ip);
    setIpLoading(true);
    try {
      const res = await api.get(`/api/admin/manifest/ip/${ip}`);
      setIpData(res.data);
      setSelectedIP(ip);
      setView('ip-detail');
    } catch { toast.error('IP lookup failed'); }
    setIpLoading(false);
  };

  const calculateThreatLevel = (ipData) => {
    if (!ipData?.entries?.length) return 'minimal';
    const entries = ipData.entries;
    const failures = entries.filter(e => e.outcome === 'failure').length;
    const failureRate = (failures / entries.length) * 100;
    const uniqueEvents = new Set(entries.map(e => e.event_type)).size;
    const hasBruteForce = entries.filter(e => e.event_type === 'login_failed').length > 5;

    let score = 0;
    if (failureRate > 80) score += 40;
    else if (failureRate > 50) score += 30;
    else if (failureRate > 30) score += 20;
    else score += 10;

    if (hasBruteForce) score += 30;
    if (uniqueEvents > 4) score += 15;
    if (entries.length > 20) score += 15;

    score = Math.min(100, score);

    if (score >= 80) return 'critical';
    if (score >= 60) return 'high';
    if (score >= 40) return 'medium';
    if (score >= 20) return 'low';
    return 'minimal';
  };

  const getThreatBarColor = (score) => {
    if (score >= 80) return '#ff0000';
    if (score >= 60) return '#ff4444';
    if (score >= 40) return '#ff9800';
    if (score >= 20) return '#ffeb3b';
    return '#4caf50';
  };

  const computeIPStats = (ipData) => {
    if (!ipData?.entries?.length) return null;
    const entries = ipData.entries;
    const successes = entries.filter(e => e.outcome === 'success').length;
    const failures = entries.filter(e => e.outcome === 'failure').length;
    const uniqueEmails = new Set(entries.filter(e => e.email).map(e => e.email)).size;
    const uniqueEventTypes = new Set(entries.map(e => e.event_type)).size;
    const firstSeen = entries[entries.length - 1]?.created_at;
    const lastSeen = entries[0]?.created_at;
    const deviceTypes = [...new Set(entries.filter(e => e.device_type).map(e => e.device_type))];
    const browsers = [...new Set(entries.filter(e => e.browser && e.browser !== 'Unknown').map(e => e.browser))];
    const eventCounts = {};
    entries.forEach(e => {
      eventCounts[e.event_type] = (eventCounts[e.event_type] || 0) + 1;
    });
    const failureRate = entries.length > 0 ? ((failures / entries.length) * 100).toFixed(1) : 0;

    return {
      successes, failures, uniqueEmails, uniqueEventTypes,
      firstSeen, lastSeen, deviceTypes, browsers, eventCounts,
      failureRate, total: entries.length,
    };
  };

  const eventTypes = [
    { value: '', label: 'All Events' },
    { value: 'login_success', label: 'Login Success' },
    { value: 'login_failed', label: 'Login Failed' },
    { value: 'register_success', label: 'Register Success' },
    { value: 'register_failed', label: 'Register Failed' },
    { value: 'oauth_login', label: 'OAuth Login' },
    { value: 'oauth_register', label: 'OAuth Register' },
    { value: 'forgot_password', label: 'Forgot Password' },
  ];

  // ── IP DETAIL VIEW ──
  if (view === 'ip-detail' && ipData) {
    const ipStats = computeIPStats(ipData);
    const threatLevel = calculateThreatLevel(ipData);
    const threatInfo = THREAT_LEVELS[threatLevel];
    const threatScore = threatLevel === 'critical' ? 95 : threatLevel === 'high' ? 70 : threatLevel === 'medium' ? 50 : threatLevel === 'low' ? 30 : 10;
    const geo = ipData.entries[0];

    return (
      <div className="page-content" style={{ maxWidth: '100%' }}>
        {/* Back button */}
        <button onClick={() => { setView('list'); setSelectedIP(null); }}
          style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'var(--space-lg)' }}>
          <ArrowLeft size={16} /> Back to Manifest
        </button>

        {/* IP Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 'var(--space-xl)' }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Globe size={24} color="var(--accent-primary)" /> {selectedIP}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
              {geo?.city && geo?.country ? `${geo.city}, ${geo.country}` : geo?.country || 'Unknown Location'}
              {geo?.isp && geo.isp !== 'Local Network' ? ` · ${geo.isp}` : ''}
            </p>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 20px', borderRadius: 12,
            background: threatInfo.bg, border: `2px solid ${threatInfo.color}`,
          }}>
            <Shield size={20} color={threatInfo.color} />
            <span style={{ color: threatInfo.color, fontWeight: 800, fontSize: 16, letterSpacing: '0.05em' }}>{threatInfo.label}</span>
          </div>
        </div>

        {/* Threat Level Bar */}
        <div style={{ marginBottom: 'var(--space-xl)', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
            <span>Threat Score</span>
            <span style={{ fontWeight: 700, color: getThreatBarColor(threatScore) }}>{threatScore}/100</span>
          </div>
          <div style={{ height: 12, borderRadius: 6, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${threatScore}%`, background: `linear-gradient(90deg, #4caf50, #ffeb3b, #ff9800, #ff4444, #ff0000)`, borderRadius: 6, transition: 'width 0.5s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--text-tertiary)' }}>
            <span>Minimal</span><span>Low</span><span>Medium</span><span>High</span><span>Critical</span>
          </div>
        </div>

        {/* KPI Grid */}
        {ipStats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
            <KPICard icon={<Hash size={18} />} label="Total Requests" value={ipStats.total} color="var(--accent-primary)" />
            <KPICard icon={<CheckCircle size={18} />} label="Successes" value={ipStats.successes} color="var(--success)" />
            <KPICard icon={<XCircle size={18} />} label="Failures" value={ipStats.failures} color="var(--error)" />
            <KPICard icon={<Target size={18} />} label="Failure Rate" value={`${ipStats.failureRate}%`} color="var(--warning)" />
            <KPICard icon={<UserCheck size={18} />} label="Unique Emails" value={ipStats.uniqueEmails} color="var(--info)" />
            <KPICard icon={<Layers size={18} />} label="Event Types" value={ipStats.uniqueEventTypes} color="var(--accent-secondary)" />
            <KPICard icon={<Monitor size={18} />} label="Devices" value={ipStats.deviceTypes.length || 1} color="var(--text-secondary)" />
            <KPICard icon={<Clock size={18} />} label="First Seen" value={ipStats.firstSeen ? new Date(ipStats.firstSeen).toLocaleDateString() : ', '} color="var(--text-secondary)" />
            <KPICard icon={<Clock size={18} />} label="Last Seen" value={ipStats.lastSeen ? new Date(ipStats.lastSeen).toLocaleDateString() : ', '} color="var(--text-secondary)" />
            <KPICard icon={<Fingerprint size={18} />} label="Fingerprint" value={geo?.fingerprint?.slice(0, 8) || ', '} color="var(--text-secondary)" />
          </div>
        )}

        {/* Event Breakdown */}
        {ipStats?.eventCounts && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 16 }}>
              <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={14} /> Event Distribution
              </h4>
              {Object.entries(ipStats.eventCounts).map(([event, count]) => (
                <div key={event} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{event.replace(/_/g, ' ')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(count / ipStats.total) * 100}%`, background: 'var(--accent-primary)', borderRadius: 3 }} />
                    </div>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600, minWidth: 20, textAlign: 'right' }}>{count}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Device & Browser Info */}
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 16 }}>
              <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Smartphone size={14} /> Device & Browser
              </h4>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                <div><strong>Devices:</strong> {ipStats.deviceTypes.join(', ') || 'Unknown'}</div>
                <div><strong>Browsers:</strong> {ipStats.browsers.join(', ') || 'Unknown'}</div>
                <div><strong>OS:</strong> {geo?.os || 'Unknown'}</div>
                <div><strong>User Agent:</strong> <span style={{ wordBreak: 'break-all', color: 'var(--text-tertiary)' }}>{geo?.user_agent || ', '}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* Entry Table */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 16 }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Request History ({ipData.total} entries)</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Outcome</th>
                  <th style={thStyle}>Error</th>
                </tr>
              </thead>
              <tbody>
                {ipData.entries.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={tdStyle}>{new Date(e.created_at).toLocaleString()}</td>
                    <td style={tdStyle}><span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{e.event_type?.replace(/_/g, ' ')}</span></td>
                    <td style={tdStyle}>{e.email || ', '}</td>
                    <td style={tdStyle}>{e.outcome === 'success' ? <CheckCircle size={14} color="var(--success)" /> : <XCircle size={14} color="var(--error)" />}</td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.error_message || ', '}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Globe size={22} color="var(--accent-primary)" /> Visitor Manifest
        </h2>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>Complete forensic log of every login, registration, and auth event</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
          <KPICard icon={<Activity size={20} />} label="Total Events" value={stats.total} color="var(--accent-primary)" />
          <KPICard icon={<CheckCircle size={20} />} label="Success" value={stats.success} color="var(--success)" />
          <KPICard icon={<XCircle size={20} />} label="Failures" value={stats.failures} color="var(--error)" />
          <KPICard icon={<Monitor size={20} />} label="Unique IPs" value={stats.unique_ips} color="var(--warning)" />
          <KPICard icon={<Globe size={20} />} label="Countries" value={stats.by_country?.length || 0} color="var(--accent-secondary)" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-lg)', flexWrap: 'wrap' }}>
        <select value={filters.event_type} onChange={e => setFilters(p => ({ ...p, event_type: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', fontSize: 13 }}>
          {eventTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filters.outcome} onChange={e => setFilters(p => ({ ...p, outcome: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
        <input placeholder="Search IP or email..." value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
          style={{ flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8, background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', fontSize: 13 }} />
      </div>

      {/* IP Quick Lookup */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-xl)' }}>
        <input value={ipLookup} onChange={e => setIpLookup(e.target.value)} placeholder="Quick IP lookup..."
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', fontSize: 13 }}
          onKeyDown={e => e.key === 'Enter' && handleIpLookup()} />
        <button onClick={handleIpLookup} disabled={ipLoading}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--accent-gradient)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} /> {ipLoading ? 'Looking...' : 'Lookup'}
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
              <th style={thStyle}>Time</th>
              <th style={thStyle}>Event</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>IP</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Device</th>
              <th style={thStyle}>Browser</th>
              <th style={thStyle}>Outcome</th>
              <th style={thStyle}>Error</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                <td style={tdStyle}>{new Date(e.created_at).toLocaleString()}</td>
                <td style={tdStyle}><span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{e.event_type?.replace(/_/g, ' ')}</span></td>
                <td style={tdStyle}>{e.email || ', '}</td>
                <td style={tdStyle}>
                  <code onClick={() => handleIpClick(e.ip_address)} style={{ fontSize: 11, color: 'var(--accent-primary)', cursor: 'pointer', textDecoration: 'underline' }}>{e.ip_address || ', '}</code>
                </td>
                <td style={tdStyle}>{e.city ? `${e.city}, ${e.country}` : (e.country || ', ')}</td>
                <td style={tdStyle}>{e.device_type || ', '}</td>
                <td style={tdStyle}>{e.browser || ', '}</td>
                <td style={tdStyle}>{e.outcome === 'success' ? <CheckCircle size={14} color="var(--success)" /> : <XCircle size={14} color="var(--error)" />}</td>
                <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.error_message || ', '}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasMore && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <button onClick={() => { setPage(p => p + 1); loadManifest(page + 1); }}
              style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>
              Load More ({total - entries.length} remaining)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ color, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap' };
